import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

import { HighlightedText } from '@/components/atoms/HighlightedText';
import { Currency } from '@/data/currencies/type';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { RecipientAccount } from '@/types/contacts';
import { Locale } from '@/types/locale';

interface MatchIndices {
  readonly name?: ReadonlyArray<readonly [number, number]>;
  readonly nickname?: ReadonlyArray<readonly [number, number]>;
}

interface Props {
  readonly account: RecipientAccount;
  readonly matchIndices?: MatchIndices;
}

export const Recipient: React.FC<Props> = (props: Props): React.ReactElement => {
  const { account, matchIndices } = props;

  const tr = useTranslator();
  const locale = React.useContext<Locale>(LocaleContext);
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();
  const currency = React.useMemo(
    (): Currency => Currency.fromCode(locale, account.currencyCode, displayUsdcAsUsd),
    [locale, account.currencyCode, displayUsdcAsUsd],
  );

  const Icon = React.useMemo(
    (): React.FunctionComponent<React.SVGProps<SVGSVGElement>> => currency.icon,
    [currency.icon],
  );

  return (
    <Box>
      <Typography variant="body2">
        <HighlightedText text={account.name} indices={matchIndices?.name} />
      </Typography>
      {account.nickname && (
        <Box my={1}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              backgroundColor: 'action.hover',
            }}>
            <Typography variant="body1" color="primary.main" fontWeight={500}>
              <HighlightedText text={account.nickname} indices={matchIndices?.nickname} />
            </Typography>
          </Box>
        </Box>
      )}
      {account.bankName && (
        <Typography variant="body1" color="text.secondary">
          Bank: {account.bankName}
        </Typography>
      )}
      {account.accountNumber && (
        <Typography variant="body1" color="text.secondary">
          Account/IBAN: {account.accountNumber}
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={1}>
        <Icon width={16} height={16} />
        <Typography variant="body1" color="text.secondary">
          {account.currency} - {tr(account.paymentMethod)}
        </Typography>
      </Box>
    </Box>
  );
};
